import clsx from 'clsx';
import { MoreHorizontal } from 'lucide-react';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';

export interface ActionsMenuItem {
  label: string;
  onSelect: () => void;
  /** Action destructrice (affichée en rouge). */
  danger?: boolean;
  disabled?: boolean;
  /** Infobulle expliquant l'effet de l'action. */
  hint?: string;
}

/**
 * Menu « ⋯ » des actions secondaires (ligne de tableau, tuile). Le menu est positionné
 * par rapport à l'écran (position fixe) pour ne pas être rogné par un conteneur qui
 * défile, et suit son bouton au défilement. Clavier : Entrée/Espace ou flèche bas pour ouvrir, flèches pour naviguer,
 * Échap pour fermer.
 */
export function ActionsMenu({ items, label }: { items: ActionsMenuItem[]; label: string }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position calculée par rapport à l'écran ; recalculée au défilement pour que le
  // menu suive son bouton (il se fermait auparavant au moindre défilement).
  const place = useCallback(() => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    const menuHeight = items.length * 36 + 8;
    // Ouvre vers le haut s'il n'y a pas la place en dessous.
    const top = r.bottom + menuHeight > window.innerHeight ? r.top - menuHeight - 4 : r.bottom + 4;
    setPos({ top, right: window.innerWidth - r.right });
  }, [items.length]);

  useLayoutEffect(() => {
    if (open) place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus();
    function onPointer(e: PointerEvent) {
      const t = e.target as Node;
      if (!menuRef.current?.contains(t) && !buttonRef.current?.contains(t)) setOpen(false);
    }
    document.addEventListener('pointerdown', onPointer);
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  function onMenuKey(e: KeyboardEvent<HTMLDivElement>) {
    const entries = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ??
        [],
    );
    const i = entries.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      entries[(i + 1) % entries.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      entries[(i - 1 + entries.length) % entries.length]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  }

  if (items.length === 0) return null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        className={clsx(
          'inline-flex h-8 w-8 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40',
          open && 'bg-slate-100 text-slate-800',
        )}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
      </button>
      {open && pos && (
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          style={{ top: pos.top, right: pos.right }}
          className="surface fixed z-40 min-w-[11rem] py-1 text-left shadow-lg"
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              disabled={item.disabled}
              title={item.hint}
              onClick={() => {
                setOpen(false);
                item.onSelect();
              }}
              className={clsx(
                'flex h-9 w-full items-center px-3 text-sm transition hover:bg-slate-100 focus:bg-slate-100 focus:outline-none disabled:opacity-50',
                item.danger ? 'text-red-600' : 'text-slate-700',
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
